import React from "react";
import { Navigate, Route } from "react-router-dom";
import { OPS_ROLES } from "../constants/roles";
import AppShell from "../layout/AppShell";
// Shared with the pharmacy console -- the two copies were identical except that both
// hardcoded a /home denial target, which 404s for anyone outside ADMIN/SUPER_ADMIN.
import RequireRoles from "../layout/RequireRoles";
import { OPS_NAV } from "./nav-config";
// Opt-in, per-route table refinements -- scoped to .ops-v2-table-refined.
import "./theme/table-refinements.css";
import TatDashboard from "./pages/tat-dashboard";
import B2BPage from "./pages/b2b";
import LegacyOrdersPage from "./pages/orders-legacy";
// Default export, unlike the four legacy pages below -- src/pages/home/index.jsx
// does `export default Home`. A named import here yields undefined and crashes.
import Home from "../../pages/home";
// Named exports, not default -- these legacy pages do `export { AllOrders }`.
// A default import here yields undefined and crashes at render.
import { FutureOrders } from "../../pages/future-orders";
import { Quotations } from "../../pages/quotations";
// Users + User Details — the legacy /users and /user/:id screens, mounted unchanged in
// every console. The users table links to `<current path>/:id`, so a row click stays in
// this console instead of jumping to the legacy /user/:id route.
import { Users } from "../../pages/users";
import UserDetails from "../../pages/user-details";
// Action Needed — the legacy /action-needed page (orders failing a pre-dispatch guardrail),
// mounted unchanged. API: GET /userOrder/admin/actionNeeded.
import ActionNeeded from "../../pages/action-needed";
// GLP-1 Dashboard — the legacy /glp1-dashboard page, mounted unchanged.
// API: GET /glp1Dashboard/admin/orders (+ POST /delivery/markDelivered/:id from its rows).
import GLP1Dashboard from "../../pages/glp1-dashboard";
// Self-Checkout Dashboard — the legacy /self-checkout-dashboard page, mounted unchanged.
// API: GET /journeyEvent/admin/dashboard.
import SelfCheckoutDashboard from "../../pages/self-checkout-dashboard";
// Order Tracking Sheet — the legacy /order-tracking-sheet page, mounted unchanged. Its API
// (pillup-api routes/api/ops/trackingSheet.js TRACKING_SHEET_ROLES) already grants every
// role this console admits.
import OrderTrackingSheet from "../../pages/order-tracking-sheet";

// Orders / Reorders / B2B / Quotations render the LEGACY page components, not v2
// rewrites. Those pages are self-contained and every provider they need
// (QueryClientProvider, ModalProvider, AddressModalProvider, the toast container)
// is already mounted above the router in src/index.js, so they mount unchanged.
// Reusing them means one implementation per screen instead of two — the parallel
// v2 rewrites had already drifted (days-left was Math.floor(x + 2) in legacy vs
// Math.ceil(x) in v2, showing different numbers for the same column).
// They are rendered with their ORIGINAL styling, deliberately: a scoped stylesheet
// once remapped their palette to the v2 tokens, but the result read as a different
// green from the real PillEngine components, so it was removed. These screens must
// look exactly like their own routes.
//
// Overview is now the FIFTH reused legacy screen: /v2/overview renders the legacy
// /home page rather than the former v2-native Overview, which has been deleted. One
// visual language, one implementation. Caveat inherited along with it: legacy /home
// is two Chart.js line charts, and only OrdersCount is fed by the API — Cohort
// renders a checked-in src/pages/home/components/charts/cohort/data.json, so that
// chart is a static snapshot, not live data.
//
// Orders is now the SIXTH: /v2/orders renders LegacyOrdersPage
// (./pages/orders-legacy), which extends legacy's OWN TableLayout/TableInstance
// (src/components/orderTable) with the prototype's extra columns, rather than
// rendering bare AllOrders (src/pages/all-orders) or the v2-native OrdersPage below.
// Neither alternative is deleted — both stay in the tree, unmounted, so either is a
// one-line swap back. See docs/plans (or git log) for why: the row rendering,
// inline Status/Channel/Source selects, ReOrder/delete/edit buttons and the
// CancellationReasonPrompt handling are all reused verbatim from legacy, not
// reimplemented — see src/v2/ops/pages/orders-legacy/table-instance.jsx.
//
// TAT is the only remaining v2-native screen; it has no legacy equivalent, so it is
// restyled to the legacy vocabulary instead of being replaced.
export const OPS_ROUTES = (
    <Route path="/v2/ops" element={<AppShell nav={OPS_NAV} />}>
        {/* Overview is the landing screen — it is the only route that is not a
            specific work queue, so a bare /v2 lands here rather than on one of them.
            It now shows the legacy /home charts (trend, not queue counts). */}
        <Route index element={<Navigate to="overview" replace />} />
        <Route
            path="overview"
            element={
                <RequireRoles roles={OPS_ROLES}>
                    <Home />
                </RequireRoles>
            }
        />
        <Route
            path="orders"
            element={
                <RequireRoles roles={OPS_ROLES}>
                    <LegacyOrdersPage />
                </RequireRoles>
            }
        />
        <Route
            path="quotations"
            element={
                <RequireRoles roles={OPS_ROLES}>
                    <Quotations />
                </RequireRoles>
            }
        />
        <Route
            path="reorders"
            element={
                <RequireRoles roles={OPS_ROLES}>
                    {/* ops-v2-table-refined is opt-in per route: it adds the
                        column spacing and numeric alignment described in
                        src/v2/ops/theme/table-refinements.css. /future-orders keeps
                        the original rendering. Add the class to the other reused
                        routes as each one is reviewed. */}
                    <div className="ops-v2-table-refined h-full">
                        <FutureOrders />
                    </div>
                </RequireRoles>
            }
        />
        <Route
            path="b2b"
            element={
                <RequireRoles roles={OPS_ROLES}>
                    <B2BPage />
                </RequireRoles>
            }
        />
        <Route
            path="tat"
            element={
                <RequireRoles roles={OPS_ROLES}>
                    <TatDashboard />
                </RequireRoles>
            }
        />
        <Route
            path="tracking-sheet"
            element={
                <RequireRoles roles={OPS_ROLES}>
                    <OrderTrackingSheet />
                </RequireRoles>
            }
        />
        <Route
            path="action-needed"
            element={
                <RequireRoles roles={OPS_ROLES}>
                    <ActionNeeded />
                </RequireRoles>
            }
        />
        <Route
            path="glp1"
            element={
                <RequireRoles roles={OPS_ROLES}>
                    <GLP1Dashboard />
                </RequireRoles>
            }
        />
        <Route
            path="self-checkout"
            element={
                <RequireRoles roles={OPS_ROLES}>
                    <SelfCheckoutDashboard />
                </RequireRoles>
            }
        />
        <Route
            path="users"
            element={
                <RequireRoles roles={OPS_ROLES}>
                    <Users />
                </RequireRoles>
            }
        />
        <Route
            path="users/:id"
            element={
                <RequireRoles roles={OPS_ROLES}>
                    <UserDetails />
                </RequireRoles>
            }
        />
    </Route>
);
